// src/features/tower/storeReadiness/components/TaskDrawer.tsx


import React, { useState } from "react";
import type { ReadinessTile, EmployeeSummary, ReadinessPermissions, SessionStage } from "../types";
import { markTaskDone, exceptTask } from "../api";

const EXCEPTION_CODES = [
  "EQUIPMENT_FAILURE",
  "UTILITY_OUTAGE",
  "STOCK_UNAVAILABLE",
  "APPROVED_LEAVE",
  "SYSTEM_ERROR",
  "OTHER",
];

type Props = {
  tile: ReadinessTile;
  branchId: string;
  date: string;
  stage: SessionStage;
  employees: EmployeeSummary[];
  permissions: ReadinessPermissions;
  onClose: () => void;
  onDone: () => void;
};

const fieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 7,
  border: "1px solid var(--ct-line)",
  fontFamily: "inherit",
  fontSize: 13,
  color: "var(--ct-ink)",
  outline: "none",
  marginBottom: 12,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--ct-ink-2)",
  display: "block",
  marginBottom: 5,
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "9px 16px",
  borderRadius: 8,
  border: "none",
  background: "var(--ct-accent)",
  color: "#fff",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  width: "100%",
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: "9px 16px",
  borderRadius: 8,
  border: "1px solid var(--ct-line)",
  background: "var(--ct-surface)",
  color: "var(--ct-ink-2)",
  fontFamily: "inherit",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  width: "100%",
};

export function TaskDrawer({ tile, branchId, date, stage, employees, permissions, onClose, onDone }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [performedByEmployeeId, setPerformedByEmployeeId] = useState("");
  const [markedByEmployeeId, setMarkedByEmployeeId] = useState("");

  const [showExceptForm, setShowExceptForm] = useState(false);
  const [exceptionCode, setExceptionCode] = useState(EXCEPTION_CODES[0]);
  const [exceptionNotes, setExceptionNotes] = useState("");

  const managers = employees.filter((e) => e.role === "Manager");
  const isFinal = tile.status === "COMPLETE" || tile.status === "EXCEPTED";

  const run = async (fn: () => Promise<any>) => {
    setSubmitting(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (e: any) {
      setError(e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkDone = () =>
    run(() =>
      markTaskDone(
        tile.taskEventId
          ? {
              taskEventId: tile.taskEventId,
              rowVersion: String(tile.rowVersion ?? "1"),
              performedByEmployeeId,
              markedByEmployeeId,
            }
          : {
              branchId,
              date,
              stage,
              taskDefinitionId: tile.taskDefinitionId,
              performedByEmployeeId,
              markedByEmployeeId,
            }
      )
    );

  const handleExcept = () =>
    run(() =>
      exceptTask(
        tile.taskEventId
          ? {
              taskEventId: tile.taskEventId,
              rowVersion: String(tile.rowVersion ?? "1"),
              exceptionCode,
              notes: exceptionNotes,
              grantedByEmployeeId: markedByEmployeeId,
            }
          : {
              branchId,
              date,
              stage,
              taskDefinitionId: tile.taskDefinitionId,
              exceptionCode,
              notes: exceptionNotes,
              grantedByEmployeeId: markedByEmployeeId,
            }
      )
    );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.5)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          border: "1px solid var(--ct-line)",
          borderRadius: 12,
          background: "var(--ct-surface)",
          padding: 22,
          boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 650, color: "var(--ct-ink)", marginBottom: 4 }}>
          {tile.taskName}
        </div>
        <div style={{ fontSize: 12, color: "var(--ct-ink-3)", marginBottom: 14 }}>
          {tile.category}
        </div>

        {tile.definitionOfDone && (
          <div
            style={{
              fontSize: 12,
              color: "var(--ct-ink-2)",
              background: "var(--ct-surface-3, #F3F4F6)",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 16,
            }}
          >
            <strong>Definition of done:</strong> {tile.definitionOfDone}
          </div>
        )}

        {error && (
          <div
            style={{
              fontSize: 12,
              color: "#991B1B",
              background: "#FEE2E2",
              borderRadius: 8,
              padding: "8px 12px",
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        {isFinal ? (
          <div style={{ fontSize: 13, color: "var(--ct-ink-3)" }}>
            This task is {tile.status === "COMPLETE" ? "done" : "excepted"}.
          </div>
        ) : !permissions.canVerifyTasks ? (
          <div style={{ fontSize: 13, color: "var(--ct-ink-3)" }}>
            Only a manager can mark this task as done.
          </div>
        ) : !showExceptForm ? (
          <>
            <label style={labelStyle}>Who did this task?</label>
            <select value={performedByEmployeeId} onChange={(e) => setPerformedByEmployeeId(e.target.value)} style={fieldStyle}>
              <option value="">Select an employee…</option>
              {employees.map((emp) => (
                <option key={emp.employeeId} value={emp.employeeId}>{emp.employeeName}</option>
              ))}
            </select>

            <label style={labelStyle}>Who's marking it done?</label>
            <select value={markedByEmployeeId} onChange={(e) => setMarkedByEmployeeId(e.target.value)} style={fieldStyle}>
              <option value="">Select a manager…</option>
              {managers.map((emp) => (
                <option key={emp.employeeId} value={emp.employeeId}>{emp.employeeName}</option>
              ))}
            </select>

            <button
              disabled={!performedByEmployeeId || !markedByEmployeeId || submitting}
              onClick={handleMarkDone}
              style={{
                ...primaryBtnStyle,
                opacity: !performedByEmployeeId || !markedByEmployeeId || submitting ? 0.5 : 1,
                cursor: !performedByEmployeeId || !markedByEmployeeId || submitting ? "not-allowed" : "pointer",
                marginBottom: 10,
              }}
            >
              {submitting ? "Saving…" : "Mark as done"}
            </button>

            {permissions.canGrantExceptions && (
              <button onClick={() => setShowExceptForm(true)} style={secondaryBtnStyle}>
                Grant an exception instead
              </button>
            )}
          </>
        ) : (
          <>
            <label style={labelStyle}>Reason</label>
            <select value={exceptionCode} onChange={(e) => setExceptionCode(e.target.value)} style={fieldStyle}>
              {EXCEPTION_CODES.map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
              ))}
            </select>

            <label style={labelStyle}>Notes (required)</label>
            <textarea
              value={exceptionNotes}
              onChange={(e) => setExceptionNotes(e.target.value)}
              rows={2}
              style={{ ...fieldStyle, resize: "vertical" }}
            />

            <label style={labelStyle}>Granted by</label>
            <select value={markedByEmployeeId} onChange={(e) => setMarkedByEmployeeId(e.target.value)} style={fieldStyle}>
              <option value="">Select a manager…</option>
              {managers.map((emp) => (
                <option key={emp.employeeId} value={emp.employeeId}>{emp.employeeName}</option>
              ))}
            </select>

            <button
              disabled={!exceptionNotes || !markedByEmployeeId || submitting}
              onClick={handleExcept}
              style={{
                ...primaryBtnStyle,
                opacity: !exceptionNotes || !markedByEmployeeId || submitting ? 0.5 : 1,
                marginBottom: 10,
              }}
            >
              {submitting ? "Granting…" : "Grant exception"}
            </button>

            <button onClick={() => setShowExceptForm(false)} style={secondaryBtnStyle}>
              Back
            </button>
          </>
        )}

        <button onClick={onClose} style={{ ...secondaryBtnStyle, marginTop: 14 }}>
          Close
        </button>
      </div>
    </div>
  );
}
